import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ContractorIntakeForm from './ContractorIntakeForm';
import { downloadCSV } from '../../utils/exportUtils';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantContractors } from '../../services/crmService'; 
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function ContractorLedger() {
  const [showIntake, setShowIntake] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { tenantId, loading: tenantLoading } = useTenant(); 

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [sortOrder, setSortOrder] = useState('HIGHEST MRR');

  // Offboarding State
  const [offboardTarget, setOffboardTarget] = useState(null);
  const [isProcessingOffboard, setIsProcessingOffboard] = useState(false);

  // Live Firebase Data State
  const [contractorsData, setContractorsData] = useState([]);

  // --- ⚡️ LIVE FIREBASE FETCH (MULTI-TENANT) ⚡️ ---
  const fetchFleet = useCallback(async () => {
    if (tenantLoading || !tenantId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const rawFleet = await getTenantContractors(tenantId);
      
      const formattedFleet = rawFleet.map(rep => {
        // Calculate compliance: If all three are true, they are cleared.
        const isCleared = rep.compliance?.w9 && rep.compliance?.nda && rep.compliance?.ica;
        
        return {
          fullId: rep.id, 
          ID: rep.id.substring(0, 8), 
          Name: `${rep.firstName || rep.first_name || 'Unknown'} ${rep.lastName || rep.last_name || ''}`.trim(),
          Email: rep.email,
          Phone: rep.phone,
          Location: rep.operatingCity ? `${rep.operatingCity}, ${rep.operatingState}` : (rep.location || 'Unknown'),
          Status: rep.status || 'Active',
          isActive: rep.isActive !== false,
          Compliance: isCleared ? 'Cleared' : 'Action Required',
          CheckHqVaulted: !!rep.checkhq_id,
          LTV: rep.metrics?.ltv || 0,
          CurrentMRR: rep.metrics?.current_mrr || 0,
          RetentionRate: rep.metrics?.retention_rate || 100,
          EstimatedPayout: rep.metrics?.ytd_payout || 0
        };
      });

      setContractorsData(formattedFleet);
    } catch (err) {
      console.error("Failed to fetch fleet ledger:", err);
      setError("Failed to sync fleet data. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, tenantLoading]);

  // Initial Load
  useEffect(() => {
    fetchFleet();
  }, [fetchFleet]);

  // Handle Modal Close & Refresh
  const handleCloseIntake = () => {
    setShowIntake(false);
    fetchFleet(); 
  };

  // --- ⚡️ OFFBOARDING PROTOCOL (SOFT DELETE) ⚡️ ---
  const handleExecuteOffboard = async () => {
    if (!offboardTarget || !tenantId) return;
    setIsProcessingOffboard(true);

    try {
      // Updates the user document, instantly killing PBAC claims without destroying ledger history
      const userRef = doc(db, 'users', offboardTarget.fullId);
      await updateDoc(userRef, {
        status: 'Terminated',
        isActive: false,
        accountType: 'TERMINATED',
        updatedAt: new Date()
      });

      await fetchFleet();
      setOffboardTarget(null);
    } catch (err) {
      console.error("Critical error during offboarding:", err);
      alert("System Error: Could not execute offboard protocol.");
    } finally {
      setIsProcessingOffboard(false);
    }
  };

  // --- Dynamic Aggregate Metrics ---
  const activeCount = contractorsData.filter(c => c.Status !== 'Terminated').length;
  const missingComplianceCount = contractorsData.filter(c => c.Status !== 'Terminated' && (c.Compliance !== 'Cleared' || !c.CheckHqVaulted)).length;
  const totalLtv = contractorsData.filter(c => c.Status !== 'Terminated').reduce((sum, c) => sum + c.LTV, 0);
  const avgRetention = activeCount > 0 
    ? (contractorsData.filter(c => c.Status !== 'Terminated').reduce((sum, c) => sum + c.RetentionRate, 0) / activeCount).toFixed(1) 
    : 0;

  // --- PERFORMANCE OPTIMIZED FILTERING ---
  const processedContractors = useMemo(() => {
    return contractorsData
      .filter(c => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = c.Name.toLowerCase().includes(query) || 
                              c.ID.toLowerCase().includes(query) || 
                              c.Location.toLowerCase().includes(query);
        
        let matchesCompliance = true;
        if (complianceFilter === 'CLEARED') matchesCompliance = c.Compliance === 'Cleared' && c.CheckHqVaulted;
        if (complianceFilter === 'ACTION REQUIRED') matchesCompliance = c.Compliance !== 'Cleared' || !c.CheckHqVaulted;
        
        let matchesStatus = true;
        if (statusFilter === 'ACTIVE') matchesStatus = c.Status !== 'Terminated';
        if (statusFilter === 'TERMINATED') matchesStatus = c.Status === 'Terminated';

        return matchesSearch && matchesCompliance && matchesStatus;
      })
      .sort((a, b) => {
        if (sortOrder === 'HIGHEST MRR') return b.CurrentMRR - a.CurrentMRR;
        if (sortOrder === 'LOWEST MRR') return a.CurrentMRR - b.CurrentMRR;
        return 0; 
      });
  }, [contractorsData, searchQuery, complianceFilter, statusFilter, sortOrder]);

  const handleExport = () => {
    const exportSchema = processedContractors.map(({ Name, Email, Phone, Location, Status, Compliance, CheckHqVaulted, CurrentMRR }) => ({
      Name, Email, Phone, Location, Status, Compliance, CheckHqVaulted: CheckHqVaulted ? 'Yes' : 'No', CurrentMRR
    }));
    downloadCSV(exportSchema, '1099_Fleet_Ledger.csv');
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative">
      {/* Header */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">1099 Contractor Fleet</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Global operational view for network contractors, compliance, and performance metrics.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleExport}
            className="text-[10px] font-bold text-slate-500 hover:text-slate-900 uppercase tracking-widest transition-colors px-4 py-2 border border-slate-300 bg-white shadow-sm flex items-center gap-2"
          >
            <i className="fa-solid fa-download"></i> Export CSV
          </button>
          <button 
            onClick={() => setShowIntake(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest border border-slate-900 shadow-sm flex items-center gap-2"
          >
            <i className="fa-solid fa-plus"></i> Onboard Contractor
          </button>
        </div>
      </div>

      {/* Fleet-Wide Aggregate Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-emerald-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Active Contractors</p>
          <p className="text-2xl font-black text-slate-800">{isLoading || tenantLoading ? '...' : activeCount}</p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-amber-500 bg-amber-50/10">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-1 flex items-center gap-2">
            Missing Compliance {missingComplianceCount > 0 && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>}
          </p>
          <p className="text-2xl font-black text-amber-700">{isLoading || tenantLoading ? '...' : missingComplianceCount}</p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-cyan-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Active LTV Generated</p>
          <p className="text-2xl font-black text-slate-800">{isLoading || tenantLoading ? '...' : `$${(totalLtv / 1000000).toFixed(1)}M`}</p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-blue-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Fleet Avg Retention</p>
          <p className="text-2xl font-black text-slate-800">{isLoading || tenantLoading ? '...' : `${avgRetention}%`}</p>
        </div>
      </div>

      {/* Control Bar: Search & Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SEARCH CONTRACTORS BY NAME, ID, OR LOCATION..." 
            className="w-full bg-white border border-slate-300 text-slate-800 text-sm font-bold pl-10 pr-4 py-3 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 placeholder:text-slate-400 placeholder:font-bold placeholder:uppercase placeholder:text-[10px] placeholder:tracking-widest"
          />
        </div>
        <select 
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-44 shadow-sm"
        >
          <option value="ACTIVE">STATUS: ACTIVE</option>
          <option value="TERMINATED">STATUS: TERMINATED</option>
          <option value="ALL">STATUS: ALL</option>
        </select>
        <select 
          value={complianceFilter}
          onChange={(e) => setComplianceFilter(e.target.value)}
          className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-48 shadow-sm"
        >
          <option value="ALL">COMPLIANCE: ALL</option>
          <option value="CLEARED">CLEARED FOR PAYOUT</option>
          <option value="ACTION REQUIRED">ACTION REQUIRED</option>
        </select>
        <select 
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-48 shadow-sm"
        >
          <option value="HIGHEST MRR">SORT: HIGHEST MRR</option>
          <option value="LOWEST MRR">SORT: LOWEST MRR</option>
        </select>
      </div>

      {/* High-Density Contractor Table */}
      <div className="bg-white border border-slate-300 shadow-sm overflow-x-auto relative">
        <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 min-w-[1200px] grid grid-cols-12 gap-4 items-center">
          <div className="col-span-3 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Contractor / ID</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Contact & Location</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Paperwork Status</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Value (LTV / Current)</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Performance</div>
          <div className="col-span-1 text-[10px] font-bold text-slate-600 uppercase tracking-widest text-right">Comp & Actions</div>
        </div>
        
        <div className="divide-y divide-slate-200 min-w-[1200px]">
          {(isLoading || tenantLoading) && (
            <div className="col-span-12 p-12 flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Syncing Fleet Data...</p>
            </div>
          )}

          {error && !(isLoading || tenantLoading) && (
            <div className="col-span-12 p-8 text-center text-red-500 text-xs font-bold uppercase tracking-widest bg-red-50">
              <i className="fa-solid fa-triangle-exclamation mr-2"></i> {error}
            </div>
          )}

          {!(isLoading || tenantLoading) && !error && processedContractors.map((contractor) => {
            const isTerminated = contractor.Status === 'Terminated';
            const isCleared = contractor.Compliance === 'Cleared' && contractor.CheckHqVaulted;
            const initials = contractor.Name.split(' ').map(n => n[0]).join('').substring(0, 2);
            const retentionNum = parseFloat(contractor.RetentionRate);

            return (
              <Link 
                key={contractor.fullId}
                to={`/contractor/${contractor.fullId}`} 
                className={`px-4 py-3 grid grid-cols-12 gap-4 items-center transition-colors group cursor-pointer ${
                  isTerminated ? 'bg-slate-50 opacity-60 hover:opacity-100 grayscale' : 
                  isCleared ? 'hover:bg-slate-50' : 'bg-amber-50/10 hover:bg-amber-50/30'
                }`}
              >
                <div className="col-span-3 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center font-black text-xs border ${
                    isTerminated ? 'bg-slate-200 text-slate-500 border-slate-300' :
                    isCleared ? 'bg-slate-900 text-white border-slate-700' : 'bg-amber-100 text-amber-700 border-amber-300'
                  }`}>
                    {initials}
                  </div>
                  <div>
                    <span className={`text-sm font-bold block transition-colors ${
                      isTerminated ? 'text-slate-600' :
                      isCleared ? 'text-slate-900 group-hover:text-emerald-700' : 'text-slate-900 group-hover:text-amber-700'
                    }`}>
                      {contractor.Name}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ID: {contractor.ID}</span>
                  </div>
                </div>
                
                <div className="col-span-2">
                  <span className="text-xs font-bold text-slate-700 block mb-0.5 truncate pr-2">{contractor.Email}</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 block truncate pr-2">{contractor.Location}</span>
                </div>
                
                <div className="col-span-2">
                  {isTerminated ? (
                    <span className="border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest mb-1 inline-block border-slate-400 text-slate-500 bg-slate-100">
                      Terminated
                    </span>
                  ) : (
                    <span className={`border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest mb-1 inline-block ${isCleared ? 'border-emerald-500 text-emerald-600 bg-emerald-50' : 'border-amber-500 text-amber-700 bg-amber-50'}`}>
                      {isCleared ? 'All Clear' : 'Action Req'}
                    </span>
                  )}
                  
                  {!isTerminated && (contractor.CheckHqVaulted ? (
                    <span className="text-[10px] font-medium block text-slate-500 mt-0.5">
                      <i className="fa-solid fa-lock text-emerald-500 mr-1"></i> CheckHQ Vaulted
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium block text-amber-600 mt-0.5 animate-pulse">
                      <i className="fa-solid fa-lock-open mr-1"></i> Awaiting Onboarding
                    </span>
                  ))}
                </div>
                
                <div className="col-span-2">
                  <span className="text-sm font-black text-emerald-700 block">${contractor.LTV.toLocaleString()} <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">LTV</span></span>
                  <span className="text-xs font-bold text-slate-600 block mt-0.5">${contractor.CurrentMRR.toLocaleString()} <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Cur. MRR</span></span>
                </div>
                
                <div className="col-span-2">
                  <span className="text-xs font-bold text-slate-800 block mb-1">Retention Rate</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-slate-200">
                      <div className={`h-full ${isTerminated ? 'bg-slate-400' : isCleared ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{width: `${retentionNum}%`}}></div>
                    </div>
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{contractor.RetentionRate}%</span>
                  </div>
                </div>
                
                <div className="col-span-1 flex flex-col items-end justify-center">
                   <span className="text-sm font-black text-slate-800 block">${contractor.EstimatedPayout.toLocaleString()}</span>
                   <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">Est. Res</span>
                   
                   {!isTerminated && (
                     <button 
                       onClick={(e) => {
                         e.preventDefault();
                         e.stopPropagation();
                         setOffboardTarget(contractor);
                       }}
                       className="text-[9px] font-bold text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 px-2 py-1 uppercase tracking-widest transition-colors shadow-sm"
                     >
                       Offboard
                     </button>
                   )}
                </div>
              </Link>
            )
          })}
          
          {!(isLoading || tenantLoading) && !error && processedContractors.length === 0 && (
            <div className="col-span-12 px-4 py-12 text-center border-2 border-dashed border-slate-200 m-4 bg-slate-50">
              <i className="fa-solid fa-users-slash text-2xl text-slate-300 mb-2 block"></i>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">No contractors match your filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* OFFBOARD CONFIRMATION MODAL */}
      {offboardTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
            <div className="bg-red-600 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-black text-white uppercase tracking-tight">Offboard Contractor</h2>
              </div>
              <button onClick={() => setOffboardTarget(null)} className="text-red-200 hover:text-white">
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            <div className="p-6 bg-slate-50 border-b border-slate-200 text-center">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-user-slash text-xl"></i>
              </div>
              <h3 className="text-xl font-black text-slate-900">{offboardTarget.Name}</h3>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">ID: {offboardTarget.ID}</p>
            </div>
            <div className="p-6 bg-white text-center space-y-4">
              <p className="text-sm font-medium text-slate-700 leading-relaxed">
                You are about to permanently revoke this contractor's platform access.
              </p>
              <div className="bg-slate-100 p-4 border border-slate-200 text-left space-y-2">
                <p className="text-xs text-slate-600"><i className="fa-solid fa-check text-emerald-500 w-4"></i> Ledger history remains intact.</p>
                <p className="text-xs text-slate-600"><i className="fa-solid fa-check text-emerald-500 w-4"></i> Trailing commissions will calculate.</p>
                <p className="text-xs text-slate-600"><i className="fa-solid fa-xmark text-red-500 w-4"></i> PBAC access instantly revoked.</p>
              </div>
            </div>
            <div className="p-6 bg-slate-100 flex gap-3">
              <button 
                onClick={() => setOffboardTarget(null)} 
                className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-3 uppercase tracking-widest hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleExecuteOffboard} 
                disabled={isProcessingOffboard}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50"
              >
                {isProcessingOffboard ? 'Processing...' : 'Execute Offboard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIntake && <ContractorIntakeForm onClose={handleCloseIntake} />}
    </div>
  );
}